/**
 * 소모품 표의 직렬화 — 표 한 장을 기존 메모 `content` 한 칸에 JSON 으로 담는다.
 *
 * 저장소는 site_consumable_notes.content(mediumtext) 그대로다. 품목별 이력이나
 * 발주까지 다루게 되면 별도 테이블로 옮겨야 하지만, 지금 필요한 건 "회사 재고
 * 얼마, 어느 현장에서 몇 개 쓰는 중" 뿐이라 한 칸으로 충분하다.
 *
 * 표 도입 전에 자유 서식으로 적어 둔 내용은 버리지 않고 `memo` 로 넘어온다.
 */

/** 표 한 줄. 회사 카드에서는 qty = 재고 수량, 현장 카드에서는 qty = 사용 중 수량. */
export interface ConsumableRow {
  name: string;
  qty: number;
  note: string;
}

export interface ConsumableSheet {
  items: ConsumableRow[];
  /** 표 도입 전 자유 서식 본문. 새로 쓰지 않고, 남아 있으면 옮겨 적도록 보여만 준다. */
  memo: string;
}

/**
 * 편집 중인 줄 — 수량이 문자열이다.
 *
 * 숫자로 들고 있으면 입력칸을 비우는 순간 0 이 되어 지웠다 다시 쓰기가 성가시다.
 * id 는 React key 용 — 인덱스를 쓰면 줄을 지울 때 입력 포커스가 옆 줄로 튄다.
 */
export interface ConsumableDraftRow {
  id: string;
  name: string;
  qty: string;
  note: string;
}

/** 이름도 비고도 없는 줄은 저장하지 않는다 — 수량만 남은 줄은 의미가 없다. */
function normalizeRow(v: unknown): ConsumableRow | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const name = typeof o.name === 'string' ? o.name.trim() : '';
  const note = typeof o.note === 'string' ? o.note.trim() : '';
  if (!name && !note) return null;
  const qty = Number(o.qty);
  return { name, qty: Number.isFinite(qty) && qty > 0 ? Math.floor(qty) : 0, note };
}

/** JSON 이 아니면(= 표 도입 전 마크다운) 본문을 통째로 memo 로 넘긴다. */
export function parseConsumableSheet(content: string | null | undefined): ConsumableSheet {
  const raw = (content ?? '').trim();
  if (!raw) return { items: [], memo: '' };
  if (!raw.startsWith('{')) return { items: [], memo: content ?? '' };
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || !Array.isArray(parsed.items)) return { items: [], memo: content ?? '' };
    return {
      items: parsed.items.map(normalizeRow).filter((r): r is ConsumableRow => r !== null),
      memo: typeof parsed.memo === 'string' ? parsed.memo : '',
    };
  } catch {
    return { items: [], memo: content ?? '' };
  }
}

/** 표도 메모도 비었으면 빈 문자열 — 백엔드는 "아직 기입 없음" 으로 다룬다. */
export function serializeConsumableSheet(sheet: ConsumableSheet): string {
  const items = sheet.items.map(normalizeRow).filter((r): r is ConsumableRow => r !== null);
  const memo = sheet.memo.trim();
  if (items.length === 0 && !memo) return '';
  return JSON.stringify(memo ? { items, memo } : { items });
}

let draftSeq = 0;

export function newConsumableDraftRow(name = ''): ConsumableDraftRow {
  draftSeq += 1;
  return { id: `cr${draftSeq}`, name, qty: '', note: '' };
}

export function toConsumableDraftRows(items: ConsumableRow[]): ConsumableDraftRow[] {
  return items.map((it) => {
    draftSeq += 1;
    return { id: `cr${draftSeq}`, name: it.name, qty: it.qty ? String(it.qty) : '', note: it.note };
  });
}

export function fromConsumableDraftRows(rows: ConsumableDraftRow[]): ConsumableRow[] {
  return rows
    .map((r) => normalizeRow({ name: r.name, qty: r.qty.replace(/[^0-9.-]/g, ''), note: r.note }))
    .filter((r): r is ConsumableRow => r !== null);
}

export const sumConsumableQty = (rows: ConsumableRow[]): number =>
  rows.reduce((acc, r) => acc + r.qty, 0);

/** 품목별 현황 한 줄 — 회사 재고와 현장 합계를 나란히 본다. */
export interface ConsumableSummaryRow {
  name: string;
  stock: number;
  inUse: number;
  /** 그 품목을 쓰는 중인 현장 수. 수량을 0 으로 적어 둔 현장도 센다. */
  siteCount: number;
}

/**
 * 회사 재고 표 + 현장별 사용중 표를 품명으로 묶는다.
 *
 * 품명이 맞아야 묶이므로 현장 표의 품명 입력칸에는 회사 품목을 추천 목록으로 준다.
 * 회사에 없는 품목도 현장에서 쓸 수 있으니 한쪽에만 있는 품명도 그대로 한 줄이 된다.
 */
export function buildConsumableSummary(
  company: ConsumableRow[],
  perSite: ConsumableRow[][],
): ConsumableSummaryRow[] {
  const byName = new Map<string, ConsumableSummaryRow>();
  const at = (name: string): ConsumableSummaryRow => {
    let row = byName.get(name);
    if (!row) {
      row = { name, stock: 0, inUse: 0, siteCount: 0 };
      byName.set(name, row);
    }
    return row;
  };

  company.filter((r) => r.name).forEach((r) => {
    at(r.name).stock += r.qty;
  });
  perSite.forEach((rows) => {
    // 한 현장이 같은 품명을 두 줄로 적어도 현장 수는 1 이다.
    const seen = new Set<string>();
    rows.filter((r) => r.name).forEach((r) => {
      const row = at(r.name);
      row.inUse += r.qty;
      if (!seen.has(r.name)) {
        seen.add(r.name);
        row.siteCount += 1;
      }
    });
  });

  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name, 'ko'));
}
